import React from 'react';
import { Link } from 'react-router-dom';
import {
  LuPalette, LuUserPen, LuTrophy, LuClipboardCheck,
  LuCrown, LuLogOut, LuChevronRight
} from 'react-icons/lu';
import {
  Avatar, IconButton, Tooltip, Menu, Typography, Box
} from '@mui/material';
import { getAvatarOrFunny } from '../../utils/avatar';
import { isParent } from '../../utils/roles';
import SubscriptionChip from './SubscriptionChip';

/* ── Small colored-icon menu item ── */
function MenuRow({ icon: Icon, iconBg, iconColor, label, desc, to, onClick, danger }) {
  const content = (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 1,
        mx: 1, my: 0.25,
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
          background: danger ? 'rgba(239,68,68,0.06)' : 'rgba(99,102,241,0.06)',
        },
        textDecoration: 'none',
        color: 'inherit',
      }}
      onClick={onClick}
      {...(to ? { component: Link, to } : {})}
    >
      {/* Icon circle */}
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: iconBg || 'rgba(99,102,241,0.08)',
        color: iconColor || '#6366f1',
        fontSize: '1.1rem',
      }}>
        <Icon />
      </Box>

      {/* Label + optional description */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.875rem', fontWeight: 550, lineHeight: 1.3,
          color: danger ? '#ef4444' : 'text.primary',
        }}>
          {label}
        </Typography>
        {desc && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
            {desc}
          </Typography>
        )}
      </Box>

      {/* Chevron */}
      {!danger && (
        <LuChevronRight style={{ fontSize: 14, color: '#cbd5e1', flexShrink: 0 }} />
      )}
    </Box>
  );
  return content;
}

export default function AccountMenu({
  user,
  accountRef,
  accountAnchorEl,
  accountMenuOpen,
  setAccountAnchorEl,
  setAccountOpen,
  isAdminPanel,
  getPlanInfo,
  onOpenPricing,
  setThemeOpen,
  handleLogout,
  t,
}) {
  const closeMenu = () => { setAccountAnchorEl(null); setAccountOpen(false); };
  const avatarSrc = getAvatarOrFunny(user);
  const displayName = user?.username || user?.name || user?.email || 'User';

  return (
    <div ref={accountRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <SubscriptionChip user={user} getPlanInfo={getPlanInfo} onOpenPricing={onOpenPricing} t={t} />

      <Tooltip title={t('header.openAccountMenu')}>
        <IconButton
          id="accountMenuButton"
          size="small"
          aria-label={t('header.openAccountMenu')}
          aria-controls={accountMenuOpen ? 'accountMenu' : undefined}
          aria-haspopup="true"
          aria-expanded={accountMenuOpen ? 'true' : undefined}
          onClick={(e) => { setAccountAnchorEl(e.currentTarget); setAccountOpen(true); }}
          sx={{ p: 0 }}
        >
          <Avatar
            alt={displayName}
            src={avatarSrc}
            sx={{ width: 32, height: 32 }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        id="accountMenu"
        anchorEl={accountAnchorEl}
        open={accountMenuOpen}
        onClose={closeMenu}
        onClick={() => {}}
        PaperProps={{
          elevation: 0,
          sx: theme => ({
            mt: 1.5,
            width: 300,
            maxHeight: { xs: '80vh', sm: 520 },
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 16px 48px rgba(0,0,0,0.5)'
              : '0 16px 48px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.04)',
            background: theme.palette.mode === 'dark' ? '#111827' : '#fff',
            '& .MuiMenu-list': { display: 'block', p: 0 },
          })
        }}
        MenuListProps={{ sx: { p: 0 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box>
          {/* ── User card header ── */}
          <Box sx={theme => ({
            p: 2, pb: 1.5,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))'
              : 'linear-gradient(135deg, #ede9fe, #e0f2fe)',
            display: 'flex', alignItems: 'center', gap: 1.5,
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
          })}>
            <Avatar
              src={avatarSrc}
              alt={displayName}
              sx={{
                width: 52, height: 52,
                border: '3px solid #fff',
                boxShadow: '0 2px 8px rgba(99,102,241,0.2)',
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }} noWrap>
                {displayName}
              </Typography>
              {user?.email && (
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>

          {/* ── Account section ── */}
          <Box sx={{ py: 0.75 }}>
            <MenuRow
              icon={LuUserPen}
              iconBg="rgba(59,130,246,0.08)"
              iconColor="#3b82f6"
              label={t('common.profile')}
              desc={t('header.menu.profileDesc', 'Edit your profile info')}
              to="/profile"
              onClick={closeMenu}
            />
            {!isAdminPanel && (
              <MenuRow
                icon={LuPalette}
                iconBg="rgba(236,72,153,0.08)"
                iconColor="#ec4899"
                label={t('common.myTheme')}
                desc={t('header.menu.themeDesc', 'Colors & appearance')}
                onClick={() => { setThemeOpen(true); closeMenu(); }}
              />
            )}
          </Box>

          {/* ── Student Zone section (hidden for parents) ── */}
          {!isAdminPanel && !isParent(user) && (
            <>
              <Box sx={theme => ({
                mx: 1.5, my: 0.5,
                borderTop: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
              })}>
                <Typography sx={{
                  fontSize: '0.68rem', fontWeight: 700, color: 'text.disabled',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  pt: 1, pb: 0.25, px: 0.5,
                }}>
                  {t('common.studentZone', 'Student Zone')}
                </Typography>
              </Box>

              <Box sx={{ pb: 0.75 }}>
                <MenuRow
                  icon={LuTrophy}
                  iconBg="rgba(245,158,11,0.1)"
                  iconColor="#f59e0b"
                  label={t('leaderboard.title', 'Leaderboard')}
                  desc={t('header.menu.leaderboardDesc', 'Rankings & achievements')}
                  to="/leaderboard"
                  onClick={closeMenu}
                />
                <MenuRow
                  icon={LuClipboardCheck}
                  iconBg="rgba(16,185,129,0.08)"
                  iconColor="#10b981"
                  label={t('practiceResults.title', 'Practice Results')}
                  desc={t('header.menu.practiceDesc', 'Your learning progress')}
                  to="/practice-results"
                  onClick={closeMenu}
                />
                <MenuRow
                  icon={LuCrown}
                  iconBg="rgba(139,92,246,0.08)"
                  iconColor="#8b5cf6"
                  label={t('membership.menu', 'Membership')}
                  desc={t('header.menu.membershipDesc', 'Plan & subscription')}
                  to="/account/membership"
                  onClick={closeMenu}
                />
              </Box>
            </>
          )}

          {/* ── Logout ── */}
          <Box sx={theme => ({
            borderTop: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            py: 0.75,
          })}>
            <MenuRow
              icon={LuLogOut}
              iconBg="rgba(239,68,68,0.06)"
              iconColor="#ef4444"
              label={t('common.logout')}
              onClick={() => { closeMenu(); handleLogout(); }}
              danger
            />
          </Box>
        </Box>
      </Menu>
    </div>
  );
}
